import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import styled, {css} from 'styled-components/native';
import TextGeneric from '../../components/textGeneric';
import {RootState} from '../../redux/store';
import {tr} from '../../translations';
import {modalTypes} from '../../utils/enums';
import {QuestionModalProps} from '../../interfaces/connected-components/questionModal';
import {closeQuestionModal} from './actions';
import GenericButton from '../../components/button';
import Animated, {
  FadeIn,
  FadeOut,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {Dimensions, Platform} from 'react-native';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';
import useResponsiveDimensions from '../../utils/hooks/useResponsiveDimensions';
import '../../utils/webAnimations';

const Container = styled.View<{isVisible: boolean; responsiveWidth: number}>`
  width: ${({responsiveWidth}) => responsiveWidth}px;
  height: 100%;
  overflow: hidden;
  align-self: center;
  align-items: center;
  justify-content: center;
  z-index: 20000;
  position: absolute;
  pointer-events: ${({isVisible}) => (isVisible ? 'auto' : 'none')};
`;
const ContentContainer = styled(Animated.View)<{
  responsiveWidth: number;
  paddingResponsive: number;
}>`
  border-radius: 8px;
  width: ${({responsiveWidth}) => responsiveWidth}px;
  align-self: center;
  background-color: ${({theme}) => theme.homeBackground};
  padding-vertical: ${({paddingResponsive}) => paddingResponsive}%;
  ${Platform.OS === 'ios'
    ? css`
        shadow-color: ${({theme}) => theme.text.dark};
        shadow-opacity: 0.2;
        shadow-radius: 4px;
        shadow-offset: 2px 4px;
      `
    : ''}

  ${Platform.OS === 'android'
    ? css`
        elevation: 4;
      `
    : ''}

  ${Platform.OS === 'web'
    ? css`
        box-shadow: 2px 4px 4px ${({theme}) => `${theme.text.dark}33`};
      `
    : ''}
  z-index: 20000;
`;
const TitleView = styled.View`
  justify-content: center;
  align-items: center;
  padding-bottom: 2%;
`;
const Title = styled(TextGeneric)<{isDark?: boolean}>`
  color: ${({theme}) => theme.questionModal.title};
  font-size: ${({theme}) => theme.text.s6}px;
  text-align: center;
  width: 88%;
  align-self: center;
`;
const MessageView = styled.View<{
  noPaddingTop?: boolean;
  paddingResponsive: number;
}>`
  border-bottom-left-radius: 7px;
  border-bottom-right-radius: 7px;
  justify-content: center;
  align-items: center;
  background-color: ${({theme}) => theme.homeBackground};
  padding-top: ${({noPaddingTop}) => (noPaddingTop ? 0 : '5%')};
  padding-horizontal: ${({paddingResponsive}) => paddingResponsive}%;
`;
const Message = styled(TextGeneric)<{paddingResponsive: number}>`
  color: ${({theme}) => theme.text.dark};
  font-size: ${({theme}) => theme.text.s8}px;
  text-align: center;
  padding-horizontal: ${({paddingResponsive}) => paddingResponsive}%;
`;
const RowView = styled.View<{isCenter?: boolean}>`
  flex-direction: row;
  align-items: center;
  justify-content: ${({isCenter}) => (isCenter ? 'center' : 'space-between')};
  padding-top: 10%;
  width: 100%;
  align-self: center;
`;
const Overlay = styled(Animated.View)`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
  background-color: rgba(0, 0, 0, 0.3);
`;
const Button = styled(GenericButton)`
  width: 48%;
  border-radius: 8px;
`;
const RejectButton = styled(GenericButton)`
  width: 48%;
  border-radius: 8px;
  border-color: ${({theme}) => theme.primary};
  border-width: 1px;
`;

const screenHeight = Dimensions.get('screen').height;

/**
 * A component that renders a question modal based on provided props. It supports both 'info' and 'question' types,
 * allowing for different interactions (e.g., confirmation, rejection).
 *
 * @param {QuestionModalProps} props - The properties passed to the component, including the question modal's state.
 * @returns A modal component configured based on the `questionModal` state from the props. The modal displays a message,
 *          and depending on its type, it offers buttons for the user to confirm or reject the modal's question or statement.
 *
 * This component is connected to a Redux store to access the `questionModal` state, allowing it to respond to state changes
 * and update the UI accordingly.
 */
const QuestionModal = ({questionModal}: QuestionModalProps) => {
  const theme = useTheme() as Theme;
  const {
    message,
    title,
    customHeaderComponent,
    isVisible,
    type,
    onConfirm,
    confirmText,
    onReject,
    rejectText,
  } = questionModal || {};
  const {wdp, getByScreenSize} = useResponsiveDimensions();
  const [modalVisible, setModalVisible] = useState(false);
  const animationController = useSharedValue(0);

  useEffect(() => {
    animationController.value = withTiming(isVisible ? 1 : 0, {duration: 300});
    if (isVisible) {
      setModalVisible(true);
    } else {
      setTimeout(() => setModalVisible(false), 300);
    }
  }, [isVisible]);

  const closeModal = (status: 'confirm' | 'reject' = 'confirm') => {
    if (status === 'confirm') {
      if (onConfirm) {
        onConfirm();
      }
      closeQuestionModal();
    } else {
      if (onReject) {
        onReject();
      }
      closeQuestionModal();
    }
  };

  const opacityModalStyle = useAnimatedStyle(() => {
    const opacityModal = interpolate(
      animationController.value,
      [0, 0.5, 1],
      [0, 0, 1],
    );
    const translateY = interpolate(
      animationController.value,
      [0, 1],
      [screenHeight, 0],
    );
    return {
      opacity: opacityModal,
      transform: [{translateY}],
    };
  });

  if (!modalVisible) {
    return null;
  }

  return (
    <Container responsiveWidth={wdp(100)} isVisible={modalVisible}>
      {isVisible && (
        <Overlay
          {...(Platform.OS === 'web'
            ? {className: 'screen-fade-in'}
            : {entering: FadeIn, exiting: FadeOut})}
          onStartShouldSetResponder={() => true}
          onResponderGrant={closeQuestionModal}
        />
      )}
      <ContentContainer
        paddingResponsive={Platform.OS === 'web' ? 2 : getByScreenSize(5, 2)}
        responsiveWidth={Platform.OS === 'web' ? wdp(30) : getByScreenSize(wdp(90), wdp(45))}
        style={opacityModalStyle}>
        {customHeaderComponent && customHeaderComponent()}
        {title &&
          (typeof title === 'function' ? (
            title()
          ) : (
            <TitleView>
              <Title isDark={!!customHeaderComponent} bold>
                {title}
              </Title>
            </TitleView>
          ))}
        <MessageView
          paddingResponsive={Platform.OS === 'web' ? 2 : getByScreenSize(5, 2)}
          noPaddingTop={!!customHeaderComponent}>
          {message &&
            (typeof message === 'function' ? (
              message()
            ) : (
              <Message paddingResponsive={Platform.OS === 'web' ? 2 : getByScreenSize(5, 2)}>
                {message}
              </Message>
            ))}
          <RowView isCenter={type === modalTypes.info}>
            {type === modalTypes.info ? (
              <Button
                label={confirmText || tr('app.ok')}
                onPress={() => closeModal()}
              />
            ) : (
              <>
                <RejectButton
                  label={rejectText || tr('app.no')}
                  labelStyle={{
                    color: theme.text.dark,
                  }}
                  onPress={() => closeModal('reject')}
                  outline
                />
                <Button
                  label={confirmText || tr('app.yes')}
                  onPress={() => closeModal()}
                />
              </>
            )}
          </RowView>
        </MessageView>
      </ContentContainer>
    </Container>
  );
};
const mapStateToProps = (state: RootState) => ({
  questionModal: state.questionModal,
});

export default connect(mapStateToProps, null)(QuestionModal);
